// src/pages/StoreLoans.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Card,
    CardContent,
    LinearProgress,
    Alert,
    Stack,
    InputAdornment,
    Tooltip,
    Avatar,
    Snackbar,
    Tabs,
    Tab,
    ToggleButton,
    ToggleButtonGroup,
    Menu,
    ListItemIcon,
    ListItemText,
    Autocomplete,
    FormHelperText
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    GetApp,
    Print,
    X,
    Payment as PaymentIcon,
    Money,
    TrendingUp,
    TrendingDown,
    CalendarMonth,
    CheckCircle,
    Warning,
    Error,
    AccountBalance,
    PictureAsPdf,
    TableChart,
    Description,
    AttachMoney,
    Receipt,
    Person,
    Phone,
    Close as CloseIcon,
    History as HistoryIcon,
    MoreVert,
    Scale
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIProductsList,
    APISupplierList,
    APIStoreLoansList,
    APIStoreLoansStore,
    APIStoreLoansUpdate,
    APIStoreLoansDelete,
    APIStoreLoanPaymentsStore,
    APIStoreLoanPaymentsListByLoan,
    APIStoreLoansExportExcel,
    APIStoreLoansExportPDF,
    APIStoreLoansPrint,
    APIUnitName
} from '../../apiServices/APIs';
import {
    getStatusById,
    recordsCount,
    recordsPerPage,
    statusData,
    formatDate,
    calculateDaysRemaining,
    getUserDetails,
    formatCreatedAtDate
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const StoreLoans = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedLoanForAction, setSelectedLoanForAction] = useState(null);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const [pageState, setPageState] = useState({
        loansData: [],
        productsData: [],
        suppliersData: [],
        unitsData: [],
        paymentsData: [],
        selectedLoanPayments: []
    });

    const [stats, setStats] = useState({
        totalLoans: 0,
        totalBorrowed: 0,
        totalPaid: 0,
        totalOutstanding: 0,
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [openViewPayments, setOpenViewPayments] = useState(false);

    // Search input states for autocomplete
    const [productSearchInput, setProductSearchInput] = useState('');
    const [supplierSearchInput, setSupplierSearchInput] = useState('');

    const [loanFormData, setLoanFormData] = useState({
        product_id: '',
        supplier_id: '',
        amount: '',
        total_amount: '',
        paid_amount: 0,
        balance: '',
        due_date: '',
        status: 1,
        description: '',
        interest_rate: 0,
    });

    const [paymentFormData, setPaymentFormData] = useState({
        loan_id: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'Cash',
        reference_no: '',
        notes: '',
    });

    const [filters, setFilters] = useState({
        supplier: '',
        status: '',
        due_date_from: '',
        due_date_to: '',
    });

    const [errors, setErrors] = useState({});
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
    }, [showToast]);

    // Fetch data
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");

            const [loansResult, productsResult, suppliersResult, unitsResult] = await Promise.all([
                apiService.list(userBranchId, APIStoreLoansList),
                apiService.list(userBranchId, APIProductsList),
                apiService.list(userBranchId, APISupplierList),
                apiService.list(userBranchId, APIUnitName)
            ]);

            if (loansResult.success && productsResult.success && suppliersResult.success) {
                const loans = Array.isArray(loansResult.data) ? loansResult.data : [];
                const products = Array.isArray(productsResult.data) ? productsResult.data : [];
                const suppliers = Array.isArray(suppliersResult.data) ? suppliersResult.data : [];
                const units = Array.isArray(unitsResult.data) ? unitsResult.data : [];

                calculateStatistics(loans);

                setPageState(prev => ({
                    ...prev,
                    loansData: loans,
                    productsData: products,
                    suppliersData: suppliers,
                    unitsData: units,
                }));
            } else {
                setError(loansResult.message || productsResult.message || suppliersResult.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate statistics
    const calculateStatistics = (loans) => {
        let totalLoans = loans.length;
        let totalBorrowed = 0;
        let totalPaid = 0;
        let totalOutstanding = 0;

        loans.forEach(loan => {
            totalBorrowed += parseFloat(loan.total_amount || 0);
            totalPaid += parseFloat(loan.paid_amount || 0);
            totalOutstanding += parseFloat(loan.balance || 0);
        });

        setStats({
            totalLoans,
            totalBorrowed,
            totalPaid,
            totalOutstanding,
        });
    };

    // Fetch payments for a loan
    const fetchLoanPayments = async (loanId) => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, `${APIStoreLoanPaymentsListByLoan}/${loanId}`);
            
            if (result.success) {
                const payments = Array.isArray(result.data) ? result.data : [];
                setPageState(prev => ({
                    ...prev,
                    selectedLoanPayments: payments
                }));
            }
        } catch (error) {
            console.error('Failed to fetch payments:', error);
        }
    };

    // Helper function to get unit name
    const getUnitName = (productId) => {
        if (!productId) return 'N/A';
        const product = pageState.productsData.find(p => p.id === productId);
        if (!product || !product.unit_id) return 'N/A';
        const unit = pageState.unitsData.find(u => u.id === product.unit_id);
        return unit ? `${unit.name} (${unit.code})` : 'N/A';
    };

    // Helper function to get unit code
    const getUnitCode = (productId) => {
        if (!productId) return 'N/A';
        const product = pageState.productsData.find(p => p.id === productId);
        if (!product || !product.unit_id) return 'N/A';
        const unit = pageState.unitsData.find(u => u.id === product.unit_id);
        return unit ? unit.code : 'N/A';
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('PRINT')) {
            showToast("Permission Denied", "You don't have permission to print loan details", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APIStoreLoansPrint, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export loan data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.supplier) formDataToSend.append('supplier_id', filters.supplier);
            if (filters.status) formDataToSend.append('status', filters.status);
            if (filters.due_date_from) formDataToSend.append('date_from', filters.due_date_from);
            if (filters.due_date_to) formDataToSend.append('date_to', filters.due_date_to);
            
            const result = await apiService.exportToExcel(APIStoreLoansExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export loan data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.supplier) formDataToSend.append('supplier_id', filters.supplier);
            if (filters.status) formDataToSend.append('status', filters.status);
            
            const result = await apiService.exportToPDF(APIStoreLoansExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter data
    const filteredData = pageState.loansData.filter(loan => {
        const matchesSearch = searchTerm === '' ||
            loan.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            loan.amount?.toString().includes(searchTerm) ||
            loan.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesSupplier = !filters.supplier || String(loan.supplier_id) === String(filters.supplier);
        const matchesStatus = !filters.status || String(loan.status) === String(filters.status);

        let matchesDueDate = true;
        if (filters.due_date_from) {
            const loanDueDate = new Date(loan.due_date).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.due_date_from).toISOString().split('T')[0];
            matchesDueDate = matchesDueDate && loanDueDate >= filterFromDate;
        }
        if (filters.due_date_to) {
            const loanDueDate = new Date(loan.due_date).toISOString().split('T')[0];
            const filterToDate = new Date(filters.due_date_to).toISOString().split('T')[0];
            matchesDueDate = matchesDueDate && loanDueDate <= filterToDate;
        }

        return matchesSearch && matchesSupplier && matchesStatus && matchesDueDate;
    });

    // Calculate totals for filtered data
    const calculateFilteredTotals = () => {
        const totalBorrowed = filteredData.reduce((sum, loan) => sum + parseFloat(loan.total_amount || 0), 0);
        const totalPaid = filteredData.reduce((sum, loan) => sum + parseFloat(loan.paid_amount || 0), 0);
        const totalOutstanding = filteredData.reduce((sum, loan) => sum + parseFloat(loan.balance || 0), 0);
        const activeLoans = filteredData.filter(loan => parseFloat(loan.balance || 0) > 0).length;
        const paidLoans = filteredData.filter(loan => parseFloat(loan.balance || 0) <= 0).length;
        const overdueLoans = filteredData.filter(loan => {
            const dueDate = new Date(loan.due_date);
            const now = new Date();
            return parseFloat(loan.balance || 0) > 0 && now > dueDate;
        }).length;

        return {
            totalBorrowed,
            totalPaid,
            totalOutstanding,
            activeLoans,
            paidLoans,
            overdueLoans,
            totalCount: filteredData.length
        };
    };

    const filteredTotals = calculateFilteredTotals();

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add loans", "error");
            return;
        }

        setPostStatus("ADD");
        setLoanFormData({
            product_id: '',
            supplier_id: '',
            amount: '',
            total_amount: '',
            paid_amount: 0,
            balance: '',
            due_date: '',
            status: 1,
            description: '',
            interest_rate: 0,
        });
        setProductSearchInput('');
        setSupplierSearchInput('');
        setActiveTab(0);
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
        setActiveTab(0);
        setProductSearchInput('');
        setSupplierSearchInput('');
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
        fetchLoanPayments(record.id);
        handleActionMenuClose();
    };

    const handleCloseView = () => {
        setOpenView(false);
        setActiveTab(0);
    };

    const handleOpenViewPayments = (record) => {
        setSelectedRecord(record);
        fetchLoanPayments(record.id);
        setOpenViewPayments(true);
        handleActionMenuClose();
    };

    const handleCloseViewPayments = () => {
        setOpenViewPayments(false);
    };

    const handleOpenPayment = (record) => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to record payments", "error");
            return;
        }

        setSelectedRecord(record);
        setPaymentFormData({
            loan_id: record.id,
            amount: '',
            payment_date: new Date().toISOString().split('T')[0],
            payment_method: 'Cash',
            reference_no: '',
            notes: '',
        });
        setPaymentDialogOpen(true);
        handleActionMenuClose();
    };

    const handleClosePayment = () => {
        setPaymentDialogOpen(false);
        setPaymentFormData({
            loan_id: '',
            amount: '',
            payment_date: new Date().toISOString().split('T')[0],
            payment_method: 'Cash',
            reference_no: '',
            notes: '',
        });
    };

    // Form handlers
    const handleLoanInputChange = (e) => {
        const { name, value } = e.target;
        setErrors(prev => ({ ...prev, [name]: false }));

        if (name === 'amount' || name === 'interest_rate') {
            const amount = name === 'amount' ? value : loanFormData.amount;
            const interestRate = name === 'interest_rate' ? value : loanFormData.interest_rate || 0;
            const totalAmount = parseFloat(amount || 0) + (parseFloat(amount || 0) * parseFloat(interestRate || 0) / 100);
            
            setLoanFormData(prev => ({
                ...prev,
                [name]: value,
                total_amount: totalAmount.toString(),
                balance: totalAmount.toString(),
            }));
        } else {
            setLoanFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handler for product selection
    const handleProductSelect = (event, selectedProduct) => {
        setLoanFormData(prev => ({
            ...prev,
            product_id: selectedProduct ? selectedProduct.id : ''
        }));
        // Auto-fill amount with product selling price
        if (selectedProduct) {
            setLoanFormData(prev => ({
                ...prev,
                amount: selectedProduct.selling?.toString() || '',
                product_id: selectedProduct.id
            }));
        }
    };

    // Handler for supplier selection
    const handleSupplierSelect = (event, selectedSupplier) => {
        setLoanFormData(prev => ({
            ...prev,
            supplier_id: selectedSupplier ? selectedSupplier.id : ''
        }));
    };

    const handlePaymentInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentFormData(prev => ({ ...prev, [name]: value }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            due_date_from: today,
            due_date_to: today
        });
        handleCloseOptions();
    };

    const applyThisMonthFilter = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters({
            ...filters,
            due_date_from: firstDay,
            due_date_to: lastDay
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            supplier: '',
            status: '',
            due_date_from: '',
            due_date_to: '',
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, loan) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedLoanForAction(loan);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedLoanForAction(null);
    };

    // Validation
    const validateLoanForm = () => {
        if (!loanFormData.product_id) {
            showToast("Validation Error", "Product is required", "warning");
            return false;
        }
        if (!loanFormData.supplier_id) {
            showToast("Validation Error", "Supplier is required", "warning");
            return false;
        }
        if (!loanFormData.amount) {
            showToast("Validation Error", "Amount is required", "warning");
            return false;
        }
        if (!loanFormData.due_date) {
            showToast("Validation Error", "Due date is required", "warning");
            return false;
        }
        if (!loanFormData.description?.trim()) {
            showToast("Validation Error", "Description is required", "warning");
            return false;
        }

        return true;
    };

    const validatePaymentForm = () => {
        if (!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0) {
            showToast("Validation Error", "Please enter a valid payment amount", "warning");
            return false;
        }
        if (parseFloat(paymentFormData.amount) > parseFloat(selectedRecord?.balance || 0)) {
            showToast("Validation Error", "Payment amount cannot exceed outstanding balance", "warning");
            return false;
        }
        return true;
    };

    // Submit handlers
    const handleSubmitLoan = async (e) => {
        e.preventDefault();

        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add loans", "error");
            return;
        }

        if (!validateLoanForm()) return;

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        Object.keys(loanFormData).forEach(key => {
            formDataToSend.append(key, loanFormData[key]);
        });

        try {
            const result = await apiService.create(APIStoreLoansStore, formDataToSend);

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                fetchData();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    const handleSubmitPayment = async () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to record payments", "error");
            return;
        }

        if (!validatePaymentForm()) return;

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));
        Object.keys(paymentFormData).forEach(key => {
            formDataToSend.append(key, paymentFormData[key]);
        });

        try {
            const result = await apiService.create(APIStoreLoanPaymentsStore, formDataToSend);

            if (result && result.success) {
                showToast("Success", "Payment recorded successfully", "success");
                handleClosePayment();
                fetchData();
                if (openView) {
                    fetchLoanPayments(selectedRecord.id);
                }
                if (openViewPayments) {
                    fetchLoanPayments(selectedRecord.id);
                }
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    // Helper functions
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getProgressPercentage = (paid, total) => {
        if (!total || total === 0) return 0;
        return Math.min(100, Math.round((paid / total) * 100));
    };

    const getStatusChip = (loan) => {
        const now = new Date();
        const dueDate = new Date(loan.due_date);
        const balance = parseFloat(loan.balance || 0);
        
        if (balance <= 0) {
            return <Chip label="Paid" color="success" size="small" icon={<CheckCircle />} />;
        } else if (now > dueDate) {
            return <Chip label="Overdue" color="error" size="small" icon={<Error />} />;
        } else {
            return <Chip label="Active" color="warning" size="small" icon={<Warning />} />;
        }
    };

    const getSupplierName = (supplierId) => {
        const supplier = pageState.suppliersData.find(s => s.id == supplierId);
        return supplier ? supplier.name : 'Unknown';
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Loan Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchData}
                title="Unable to Load Loan Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Title */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 4,
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccountBalance color="primary" sx={{ fontSize: 30, mr: 2 }} />
                    <Typography sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main
                    }}>
                        Store Loans Management
                    </Typography>
                </Box>

                <Typography variant="subtitle1" sx={{
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                    width: { xs: '100%', sm: 'auto' },
                    mb: { xs: 1, sm: 0 }
                }}>
                    {recordsCount(pageState.loansData.length)} Total Loans
                </Typography>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Loans</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {stats.totalLoans}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 48, height: 48 }}>
                                <Money sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Borrowed</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {formatCurrency(stats.totalBorrowed)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 48, height: 48 }}>
                                <TrendingUp sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Amount Paid</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                    {formatCurrency(stats.totalPaid)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.info.light, width: 48, height: 48 }}>
                                <Money sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Outstanding</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                    {formatCurrency(stats.totalOutstanding)}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.error.light, width: 48, height: 48 }}>
                                <TrendingDown sx={{ fontSize: 24 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Loan Management
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Add Loan
                            </Button>
                        )}

                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                View Options
                            </Button>
                        )}

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            Refresh
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list">
                                <ViewIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search loans..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.supplier || filters.status || filters.due_date_from || filters.due_date_to) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.supplier && (
                            <Chip
                                label={`Supplier: ${getSupplierName(filters.supplier)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, supplier: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${getStatusById(filters.status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.due_date_from && (
                            <Chip
                                label={`From: ${formatDate(filters.due_date_from)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, due_date_from: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        {filters.due_date_to && (
                            <Chip
                                label={`To: ${formatDate(filters.due_date_to)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, due_date_to: '' }))}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedLoanForAction && handleOpenView(selectedLoanForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View Details</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedLoanForAction && handlePrint(selectedLoanForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={() => selectedLoanForAction && handleOpenViewPayments(selectedLoanForAction)}>
                    <ListItemIcon><HistoryIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText sx={{ color: 'info.main' }}>Payment History</ListItemText>
                </MenuItem>
                {hasPermission('ADD') && selectedLoanForAction && parseFloat(selectedLoanForAction.balance || 0) > 0 && (
                    <MenuItem onClick={() => selectedLoanForAction && handleOpenPayment(selectedLoanForAction)}>
                        <ListItemIcon><PaymentIcon fontSize="small" color="success" /></ListItemIcon>
                        <ListItemText sx={{ color: 'success.main' }}>Record Payment</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Loans Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                                    <TableCell sx={{ color: 'white' }}>#</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Supplier</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Amount</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Progress</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Balance</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Due Date</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Status</TableCell>
                                    <TableCell sx={{ color: 'white' }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((loan, index) => {
                                    const progress = getProgressPercentage(loan.paid_amount, loan.total_amount);
                                    const daysRemaining = calculateDaysRemaining(loan.due_date);
                                    const isPaid = parseFloat(loan.balance || 0) <= 0;
                                    
                                    return (
                                        <TableRow key={loan.id} hover>
                                            <TableCell>
                                                <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Person color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {getSupplierName(loan.supplier_id)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {loan.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Stack>
                                                    <Typography variant="body2">
                                                        <strong>Total:</strong> {formatCurrency(loan.total_amount)}
                                                    </Typography>
                                                    <Typography variant="caption" color="success.main">
                                                        Paid: {formatCurrency(loan.paid_amount)}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={progress}
                                                            color={progress === 100 ? "success" : progress > 70 ? "warning" : "primary"}
                                                            sx={{ height: 8, borderRadius: 4 }}
                                                        />
                                                    </Box>
                                                    <Typography variant="body2" sx={{ minWidth: 40 }}>
                                                        {progress}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={formatCurrency(loan.balance)}
                                                    color={loan.balance > 0 ? "error" : "success"}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CalendarMonth fontSize="small" color="action" />
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {formatDate(loan.due_date)}
                                                        </Typography>
                                                        {daysRemaining > 0 && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {daysRemaining} days remaining
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {getStatusChip(loan)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Actions">
                                                    <IconButton 
                                                        sx={{ color: theme.palette.action.main }}
                                                        onClick={(e) => handleActionMenuOpen(e, loan)}
                                                        size="small"
                                                    >
                                                        <MoreVert />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            
                            {/* Footer with Totals */}
                            {filteredData.length > 0 && (
                                <TableBody>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.light + '20',
                                        borderTop: '3px solid',
                                        borderTopColor: theme.palette.primary.main,
                                        '& td': {
                                            fontWeight: 'bold',
                                            py: 2.5
                                        }
                                    }}>
                                        <TableCell colSpan={2} align="right">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                Totals ({filteredTotals.totalCount} loans):
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                    Borrowed: {formatCurrency(filteredTotals.totalBorrowed)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Paid: {formatCurrency(filteredTotals.totalPaid)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                Avg Progress: {filteredTotals.totalBorrowed > 0 
                                                    ? Math.round((filteredTotals.totalPaid / filteredTotals.totalBorrowed) * 100) 
                                                    : 0}%
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatCurrency(filteredTotals.totalOutstanding)}
                                                color="error"
                                                variant="outlined"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell colSpan={2}>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    icon={<CheckCircle />}
                                                    label={`${filteredTotals.paidLoans} Paid`}
                                                    size="small"
                                                    color="success"
                                                />
                                                <Chip
                                                    icon={<Warning />}
                                                    label={`${filteredTotals.activeLoans} Active`}
                                                    size="small"
                                                    color="warning"
                                                />
                                                {filteredTotals.overdueLoans > 0 && (
                                                    <Chip
                                                        icon={<Error />}
                                                        label={`${filteredTotals.overdueLoans} Overdue`}
                                                        size="small"
                                                        color="error"
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>

                    {filteredData.length === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                            <AccountBalance sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} />
                            <Typography>No loans found</Typography>
                            <Typography variant="body2">Try adjusting your search or filters</Typography>
                        </Box>
                    )}

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </motion.div>

            {/* Add Loan Dialog with Searchable Product and Supplier */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Add New Loan</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmitLoan} sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                            Loan Information
                        </Typography>
                        
                        {/* Product Selection with Autocomplete */}
                        <Autocomplete
                            id="product-select"
                            options={pageState.productsData.filter(p => p.payment === 'Loan')}
                            loading={loading}
                            value={pageState.productsData.find(p => p.id === loanFormData.product_id) || null}
                            onChange={handleProductSelect}
                            inputValue={productSearchInput}
                            onInputChange={(event, newInputValue) => {
                                setProductSearchInput(newInputValue);
                            }}
                            getOptionLabel={(option) => `${option.name} - ${formatCurrency(option.selling)}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box>
                                        <Typography variant="body1">
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Price: {formatCurrency(option.selling)} | 
                                            Unit: {getUnitName(option.id)} | 
                                            Stock: {option.stock}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Product *"
                                    placeholder="Search product..."
                                    required
                                    error={!!errors.product_id}
                                    helperText={errors.product_id ? "Product is required" : ""}
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        {/* Supplier Selection with Autocomplete */}
                        <Autocomplete
                            id="supplier-select"
                            options={pageState.suppliersData}
                            loading={loading}
                            value={pageState.suppliersData.find(s => s.id === loanFormData.supplier_id) || null}
                            onChange={handleSupplierSelect}
                            inputValue={supplierSearchInput}
                            onInputChange={(event, newInputValue) => {
                                setSupplierSearchInput(newInputValue);
                            }}
                            getOptionLabel={(option) => `${option.name} - ${option.phone}`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box>
                                        <Typography variant="body1">
                                            {option.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Phone: {option.phone} | Location: {option.location}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Supplier *"
                                    placeholder="Search supplier..."
                                    required
                                    error={!!errors.supplier_id}
                                    helperText={errors.supplier_id ? "Supplier is required" : ""}
                                    sx={{ mb: 2 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <TextField
                            fullWidth
                            label="Amount *"
                            name="amount"
                            type="number"
                            value={loanFormData.amount}
                            onChange={handleLoanInputChange}
                            required
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AttachMoney />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Interest Rate (%)"
                            name="interest_rate"
                            type="number"
                            value={loanFormData.interest_rate}
                            onChange={handleLoanInputChange}
                            sx={{ mb: 2 }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <TrendingUp />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Total Amount"
                            name="total_amount"
                            type="number"
                            value={loanFormData.total_amount}
                            disabled
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Money />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Due Date *"
                            name="due_date"
                            type="date"
                            value={loanFormData.due_date}
                            onChange={handleLoanInputChange}
                            required
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Description *"
                            name="description"
                            multiline
                            rows={3}
                            value={loanFormData.description}
                            onChange={handleLoanInputChange}
                            required
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Description />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAdd} color="error" variant="contained">Cancel</Button>
                    <Button onClick={handleSubmitLoan} color="primary" variant="contained">
                        Add Loan
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Payment Dialog */}
            <Dialog
                open={paymentDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleClosePayment();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Record Payment</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Loan Balance:</strong> {formatCurrency(selectedRecord.balance)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Due Date:</strong> {formatDate(selectedRecord.due_date)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Product:</strong> {pageState.productsData.find(p => p.id === selectedRecord.product_id)?.name || 'N/A'}
                            </Typography>
                        </Alert>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Payment Amount *"
                            name="amount"
                            type="number"
                            value={paymentFormData.amount}
                            onChange={handlePaymentInputChange}
                            required
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AttachMoney />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Payment Date"
                            name="payment_date"
                            type="date"
                            value={paymentFormData.payment_date}
                            onChange={handlePaymentInputChange}
                            required
                            InputLabelProps={{ shrink: true }}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Payment Method</InputLabel>
                            <Select
                                name="payment_method"
                                value={paymentFormData.payment_method}
                                onChange={handlePaymentInputChange}
                                label="Payment Method"
                            >
                                <MenuItem value="Cash">Cash</MenuItem>
                                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                                <MenuItem value="Cheque">Cheque</MenuItem>
                                <MenuItem value="Mobile Money">Mobile Money</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Reference No."
                            name="reference_no"
                            value={paymentFormData.reference_no}
                            onChange={handlePaymentInputChange}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Receipt />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Notes"
                            name="notes"
                            multiline
                            rows={2}
                            value={paymentFormData.notes}
                            onChange={handlePaymentInputChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Description />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePayment} color="error" variant="contained">Cancel</Button>
                    <Button onClick={handleSubmitPayment} color="primary" variant="contained">
                        Record Payment
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Loan Details Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Loan Details - #{selectedRecord?.id}</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Details" icon={<Description />} iconPosition="start" />
                                <Tab label="Payments" icon={<PaymentIcon />} iconPosition="start" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        mb: 3,
                                        p: 2,
                                        backgroundColor: theme.palette.grey[100],
                                        borderRadius: 2
                                    }}>
                                        <Avatar sx={{ 
                                            backgroundColor: theme.palette.primary.light,
                                            color: theme.palette.primary.main,
                                            mr: 2
                                        }}>
                                            <Person />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {getSupplierName(selectedRecord.supplier_id)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedRecord.description}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Product: {pageState.productsData.find(p => p.id === selectedRecord.product_id)?.name || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Loan Amount</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                                    {formatCurrency(selectedRecord.amount)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    {formatCurrency(selectedRecord.total_amount)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                    {formatCurrency(selectedRecord.paid_amount)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Balance</Typography>
                                                <Typography variant="h6" sx={{ 
                                                    fontWeight: 'bold',
                                                    color: selectedRecord.balance > 0 ? 'error.main' : 'success.main'
                                                }}>
                                                    {formatCurrency(selectedRecord.balance)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {selectedRecord.interest_rate || 0}%
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Due Date</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {formatDate(selectedRecord.due_date)}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                                {getStatusChip(selectedRecord)}
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="body2" color="text.secondary">Progress</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={getProgressPercentage(selectedRecord.paid_amount, selectedRecord.total_amount)}
                                                        color={selectedRecord.balance <= 0 ? "success" : "primary"}
                                                        sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                                                    />
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {getProgressPercentage(selectedRecord.paid_amount, selectedRecord.total_amount)}%
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    {/* System Information - Card Style */}
                                    <SystemInformation
                                        createdBy={selectedRecord.created_by}
                                        updatedBy={selectedRecord.updated_by}
                                        createdAt={selectedRecord.created_at}
                                        updatedAt={selectedRecord.updated_at}
                                        title="Store Loan System Information"
                                    />
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Payment History
                                    </Typography>

                                    {pageState.selectedLoanPayments.length > 0 ? (
                                        <>
                                            <TableContainer component={Paper} elevation={0}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ 
                                                            backgroundColor: theme.palette.primary.main,
                                                            '& th': {
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.95rem'
                                                            }
                                                        }}>
                                                            <TableCell>Date</TableCell>
                                                            <TableCell>Amount</TableCell>
                                                            <TableCell>Method</TableCell>
                                                            <TableCell>Reference #</TableCell>
                                                            <TableCell>Notes</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {pageState.selectedLoanPayments.map((payment, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>
                                                                    {formatCreatedAtDate(payment.payment_date || payment.created_at)}
                                                                </TableCell>
                                                                <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                                    {formatCurrency(payment.amount)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={payment.payment_method?.toUpperCase()}
                                                                        color={payment.payment_method === 'Cash' ? 'success' : 
                                                                               payment.payment_method === 'Bank Transfer' ? 'primary' : 'secondary'}
                                                                        size="small"
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {payment.reference_no || 'N/A'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {payment.notes || 'N/A'}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>

                                            <Box sx={{ 
                                                p: 2, 
                                                backgroundColor: theme.palette.info.light + '20',
                                                borderRadius: 2,
                                                mt: 2
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                        Total Paid:
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                        {formatCurrency(selectedRecord.paid_amount)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                                        Remaining Balance:
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ 
                                                        fontWeight: 'bold',
                                                        color: selectedRecord.balance > 0 ? 'error.main' : 'success.main'
                                                    }}>
                                                        {formatCurrency(selectedRecord.balance)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </>
                                    ) : (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">
                                                No payment history found for this loan
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                    {hasPermission('ADD') && selectedRecord && parseFloat(selectedRecord.balance || 0) > 0 && (
                        <Button
                            onClick={() => {
                                handleCloseView();
                                handleOpenPayment(selectedRecord);
                            }}
                            color="success"
                            variant="contained"
                            startIcon={<PaymentIcon />}
                        >
                            Record Payment
                        </Button>
                    )}
                    <Button onClick={handleCloseView} color="error" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Payment History Dialog */}
            <Dialog
                open={openViewPayments}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseViewPayments();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Payment History - {selectedRecord ? getSupplierName(selectedRecord.supplier_id) : ''}</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <>
                            <Box sx={{ mb: 3, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Amount
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {formatCurrency(selectedRecord.total_amount)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Paid
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                            {formatCurrency(selectedRecord.paid_amount)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Remaining Balance
                                        </Typography>
                                        <Typography variant="h6" sx={{ 
                                            fontWeight: 'bold',
                                            color: selectedRecord.balance > 0 ? 'error.main' : 'success.main'
                                        }}>
                                            {formatCurrency(selectedRecord.balance)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Payment History
                            </Typography>
                            
                            {pageState.selectedLoanPayments.length > 0 ? (
                                <TableContainer component={Paper} elevation={0}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ 
                                                backgroundColor: theme.palette.primary.main,
                                                '& th': {
                                                    color: 'white',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.95rem'
                                                }
                                            }}>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Amount</TableCell>
                                                <TableCell>Method</TableCell>
                                                <TableCell>Reference #</TableCell>
                                                <TableCell>Notes</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pageState.selectedLoanPayments.map((payment, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {formatCreatedAtDate(payment.payment_date || payment.created_at)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                        {formatCurrency(payment.amount)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={payment.payment_method?.toUpperCase()}
                                                            color={payment.payment_method === 'Cash' ? 'success' : 
                                                                   payment.payment_method === 'Bank Transfer' ? 'primary' : 'secondary'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.reference_no || 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.notes || 'N/A'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color="text.secondary">
                                        No payment history found for this loan
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {hasPermission('ADD') && selectedRecord && parseFloat(selectedRecord.balance || 0) > 0 && (
                        <Button
                            onClick={() => {
                                handleCloseViewPayments();
                                handleOpenPayment(selectedRecord);
                            }}
                            color="success"
                            variant="contained"
                            startIcon={<PaymentIcon />}
                        >
                            Record Payment
                        </Button>
                    )}
                    <Button onClick={handleCloseViewPayments} color="error" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Loans
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Due Loans
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            onClick={applyThisMonthFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show This Month's Due Loans
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Fields
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>Supplier</InputLabel>
                            <Select
                                name="supplier"
                                value={filters.supplier}
                                onChange={handleFilterChange}
                                label="Supplier"
                            >
                                <MenuItem value="">All Suppliers</MenuItem>
                                {pageState.suppliersData.map(supplier => (
                                    <MenuItem key={supplier.id} value={supplier.id}>
                                        {supplier.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="1">Active</MenuItem>
                                <MenuItem value="2">Paid</MenuItem>
                                <MenuItem value="3">Overdue</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Due Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Due Date From"
                                    type="date"
                                    name="due_date_from"
                                    value={filters.due_date_from}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Due Date To"
                                    type="date"
                                    name="due_date_to"
                                    value={filters.due_date_to}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default StoreLoans;